import React, { useState } from 'react'
import { CustomMessageOrderMetaDetails } from '../GGCustomMessageTypes'
import { Alert, Button, Typography, Stack, useMediaQuery, useTheme } from '@mui/material'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import { useNavigate } from 'react-router-dom'
import { SessionManager } from '../../../../services/SessionManager/SessionManager'
import { OrderAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/OrderAPIHandler'
import { OrderStatus } from '../../../../Models/Order'
import { ErrorLogger } from '../../../../utils/Logger'

const SELLER_ORDERS_URL = '/freelancer-dashboard/orders'
const SELLER_WORKSPACE_URL = '/freelancer-dashboard/workspace'

interface OrderCompletedCardProps {
    orderId: string
    orderCompletionTimeStamp: number
    orderMetaData: CustomMessageOrderMetaDetails
}

const OrderCompletedCard = ({
    orderId,
    orderCompletionTimeStamp,
    orderMetaData
}: OrderCompletedCardProps) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isCurrentUserBuyer = orderMetaData.buyerId === SessionManager.shared.buyer?.id
    const [isResolvingDestination, setIsResolvingDestination] = useState(false);

    const resolveSellerDestination = async (): Promise<string> => {
        try {
            const sellerOrders = await OrderAPIHandler.shared.getOrdersBySellerId();
            const sellerOrder = sellerOrders.find((order) => order.id === orderId);
            const isArchived =
                sellerOrder?.status === OrderStatus.completed ||
                sellerOrder?.status === OrderStatus.cancelled;
            return isArchived ? SELLER_ORDERS_URL : SELLER_WORKSPACE_URL;
        } catch (error) {
            ErrorLogger.log(error, 'OrderCompletedCard.resolveSellerDestination');
            return SELLER_WORKSPACE_URL;
        }
    };

    const handleViewOrderClick = async () => {
        if (isCurrentUserBuyer) {
            navigate('/order-details/' + orderId);
            return;
        }

        setIsResolvingDestination(true);
        try {
            navigate(await resolveSellerDestination());
        } finally {
            setIsResolvingDestination(false);
        }
    };

    const formattedDate = new Date(orderCompletionTimeStamp).toLocaleDateString(
        'en-GB',
        {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }
    ).replace(/ /g, '-')

    const getResponsiveAlertStyles = () => ({
        border: '1px solid #002D6E',
        borderRadius: isMobile ? '12px' : '16px',
        backgroundColor: '#D9F6FF',
        color: '#002D6E',
        padding: isMobile ? '16px' : '24px 24px',
        textAlign: 'center',
        minWidth: isMobile ? '100%' : 600,
        margin: isMobile ? "10px 0px" : "20px 0px",
        maxWidth: '100%',
        width: '100%'
    });

    const getResponsiveTypographyStyles = (variant: 'h6' | 'body1') => ({
        fontSize: variant === 'h6' ? (isMobile ? '18px' : '24px') : (isMobile ? '16px' : '20px'),
        fontWeight: variant === 'h6' ? 600 : 400,
        color: '#002D6E',
        fontStyle: variant === 'h6' ? 'italic' : 'normal',
        paddingBottom: variant === 'body1' ? (isMobile ? "12px" : "20px") : undefined
    });

    const getResponsiveButtonStyles = () => ({
        borderColor: '#002D6E',
        color: '#002D6E',
        backgroundColor: "white",
        fontWeight: 500,
        borderRadius: isMobile ? '6px' : '8px',
        padding: isMobile ? '4px 12px' : '6px 16px',
        textTransform: 'none',
        fontSize: isMobile ? '12px' : '14px',
        '&:hover': {
            borderColor: '#001F4D',
            backgroundColor: '#e4f2f5',
        }
    });

    return (
        <Alert
            icon={false}
            className='center'
            sx={getResponsiveAlertStyles()}
        >
            <Stack spacing={isMobile ? 0.5 : 1} alignItems="center">
                <Typography
                    variant="h6"
                    sx={getResponsiveTypographyStyles('h6')}
                >
                    Order Completed Successfully!
                </Typography>

                <Typography
                    sx={getResponsiveTypographyStyles('body1')}
                >
                    {formattedDate}
                </Typography>

                <Button
                    variant="outlined"
                    startIcon={<Inventory2OutlinedIcon />}
                    sx={getResponsiveButtonStyles()}
                    disabled={isResolvingDestination}
                    onClick={handleViewOrderClick}
                >
                    View Order
                </Button>
            </Stack>
        </Alert>
    )
}

export default OrderCompletedCard
